import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ArrowLeft,
  BarChart3,
  TrendingUp,
  DollarSign,
  PiggyBank,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  Info,
  Building2,
  Users,
  RefreshCw,
  ChevronRight,
  ArrowUpRight,
  ArrowDownRight,
  Target,
  Calendar,
  Shield,
  Bell,
  X,
  Settings,
  Clock,
  Mail,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AnalyticsDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { hoaId } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [summary, setSummary] = useState(null);
  const [benchmarkData, setBenchmarkData] = useState(null);
  const [levyForecast, setLevyForecast] = useState(null);
  const [reservePlan, setReservePlan] = useState(null);
  const [assetForecast, setAssetForecast] = useState(null);
  const [contractorPrices, setContractorPrices] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [showAlertSettings, setShowAlertSettings] = useState(false);
  const [alertSettings, setAlertSettings] = useState({
    thresholds: {
      cost_benchmark_threshold: 15,
      reserve_fund_critical: 30,
      reserve_fund_warning: 50,
      levy_collection_warning: 85,
      asset_urgent_count: 2,
    },
    notifications: {
      email_alerts: true,
      push_alerts: true,
    },
  });
  const [digestSettings, setDigestSettings] = useState({
    enabled: false,
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    hour: 8,
    recipients: [],
    include_summary: true,
    include_trends: true,
    include_recommendations: true,
  });
  const [sendingDigest, setSendingDigest] = useState(false);
  const [trendsData, setTrendsData] = useState(null);
  const [loadingTrends, setLoadingTrends] = useState(false);
  
  // Forecast form state
  const [forecastInputs, setForecastInputs] = useState({
    currentLevy: 2500,
    inflationRate: 6,
    yearsAhead: 5,
    includeReserve: true,
  });
  
  // Get effective HOA ID
  const effectiveHoaId = hoaId || user?.hoa_id;
  
  useEffect(() => {
    if (effectiveHoaId) {
      fetchAnalyticsData();
    }
  }, [effectiveHoaId]);
  
  const fetchAnalyticsData = async () => {
    setLoading(true);
    try {
      // Fetch summary first
      const summaryRes = await fetch(`${API_URL}/api/analytics/dashboard-summary/${effectiveHoaId}`, {
        credentials: 'include'
      });
      if (summaryRes.ok) {
        const data = await summaryRes.json();
        setSummary(data);
        setForecastInputs(prev => ({
          ...prev,
          currentLevy: data.summary?.levy_forecast?.current_levy || 2500
        }));
      }
      
      // Fetch other data in parallel
      const [benchmarkRes, levyRes, reserveRes, assetRes, contractorRes, alertsRes] = await Promise.all([
        fetch(`${API_URL}/api/analytics/maintenance-benchmark/${effectiveHoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/levy-forecast-quick/${effectiveHoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/reserve-plan/${effectiveHoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/asset-forecast/${effectiveHoaId}`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/contractor-prices`, { credentials: 'include' }),
        fetch(`${API_URL}/api/analytics/alerts/${effectiveHoaId}`, { credentials: 'include' }),
      ]);
      
      if (benchmarkRes.ok) setBenchmarkData(await benchmarkRes.json());
      if (levyRes.ok) setLevyForecast(await levyRes.json());
      if (reserveRes.ok) setReservePlan(await reserveRes.json());
      if (assetRes.ok) setAssetForecast(await assetRes.json());
      if (contractorRes.ok) setContractorPrices(await contractorRes.json());
      if (alertsRes.ok) {
        const alertData = await alertsRes.json();
        setAlerts(alertData.alerts || []);
      }
      
      // Fetch digest schedule
      const digestRes = await fetch(`${API_URL}/api/analytics/digest/${effectiveHoaId}/schedule`, { credentials: 'include' });
      if (digestRes.ok) {
        const digestData = await digestRes.json();
        setDigestSettings(digestData);
      }
      
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };
  
  const runCustomForecast = async () => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/levy-forecast/${effectiveHoaId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          current_levy: forecastInputs.currentLevy,
          inflation_rate: forecastInputs.inflationRate,
          years_ahead: forecastInputs.yearsAhead,
          include_reserve_buildup: forecastInputs.includeReserve,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setLevyForecast(prev => ({ ...prev, forecast: data.forecast }));
      }
    } catch (error) {
      console.error('Error running forecast:', error);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-ZA', {
      style: 'currency',
      currency: 'ZAR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
      case 'below_avg':
        return 'text-emerald-600 bg-emerald-50';
      case 'attention':
      case 'average':
      case 'needs_improvement':
        return 'text-amber-600 bg-amber-50';
      case 'critical':
      case 'above_avg':
      case 'warning':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-slate-600 bg-slate-50';
    }
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy':
      case 'ok':
      case 'below_avg':
        return <CheckCircle2 className="w-4 h-4" />;
      case 'critical':
      case 'above_avg':
      case 'warning':
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Info className="w-4 h-4" />;
    }
  };
  
  const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#6b7280'];
  
  const dismissAlert = async (alertId, days = 7) => {
    try {
      const res = await fetch(`${API_URL}/api/analytics/alerts/${effectiveHoaId}/dismiss/${alertId}?days=${days}`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        setAlerts(prev => prev.filter(a => a.alert_id !== alertId));
        toast.success(`Alert dismissed for ${days} days`);
      }
    } catch (error) {
      console.error('Error dismissing alert:', error);
      toast.error('Failed to dismiss alert');
    }
  };
  
  const saveAlertSettings = async () => {
    try {
      // Save alert thresholds
      const res = await fetch(`${API_URL}/api/analytics/alerts/${effectiveHoaId}/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          thresholds: alertSettings.thresholds,
          subscription: alertSettings.notifications,
        }),
      });
      
      // Save digest schedule
      await fetch(`${API_URL}/api/analytics/digest/${effectiveHoaId}/schedule`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(digestSettings),
      });
      
      if (res.ok) {
        toast.success('Settings saved successfully');
        setShowAlertSettings(false);
        // Refresh alerts with new thresholds
        const alertsRes = await fetch(`${API_URL}/api/analytics/alerts/${effectiveHoaId}`, { credentials: 'include' });
        if (alertsRes.ok) {
          const alertData = await alertsRes.json();
          setAlerts(alertData.alerts || []);
        }
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    }
  };
  
  const sendDigestNow = async () => {
    setSendingDigest(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/digest/${effectiveHoaId}/send`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Digest sent to ${data.recipients?.length || 1} recipient(s)`);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to send digest');
      }
    } catch (error) {
      console.error('Error sending digest:', error);
      toast.error('Failed to send digest');
    } finally {
      setSendingDigest(false);
    }
  };
  
  const getAlertTypeIcon = (type) => {
    switch (type) {
      case 'cost_benchmark':
        return <DollarSign className="w-5 h-5" />;
      case 'reserve_fund':
        return <PiggyBank className="w-5 h-5" />;
      case 'levy_collection':
        return <TrendingUp className="w-5 h-5" />;
      case 'asset_replacement':
        return <Wrench className="w-5 h-5" />;
      default:
        return <AlertTriangle className="w-5 h-5" />;
    }
  };
  
  const fetchTrendsData = async () => {
    setLoadingTrends(true);
    try {
      const res = await fetch(`${API_URL}/api/analytics/trends/${effectiveHoaId}?months=6`, {
        credentials: 'include'
      });
      if (res.ok) {
        const data = await res.json();
        setTrendsData(data);
      }
    } catch (error) {
      console.error('Error fetching trends:', error);
    } finally {
      setLoadingTrends(false);
    }
  };
  
  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <ArrowUpRight className="w-4 h-4" />;
      case 'down':
        return <ArrowDownRight className="w-4 h-4" />;
      default:
        return <TrendingUp className="w-4 h-4" />;
    }
  };
  
  const getTrendColor = (direction, isPositive = false) => {
    // For costs, down is good. For collection/reserves, up is good
    if (isPositive) {
      return direction === 'up' ? 'text-emerald-600 bg-emerald-50' : 
             direction === 'down' ? 'text-red-600 bg-red-50' : 
             'text-slate-600 bg-slate-50';
    }
    return direction === 'down' ? 'text-emerald-600 bg-emerald-50' : 
           direction === 'up' ? 'text-red-600 bg-red-50' : 
           'text-slate-600 bg-slate-50';
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50" data-testid="analytics-dashboard">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900 flex items-center gap-2">
                  <BarChart3 className="w-6 h-6 text-blue-600" />
                  Analytics Engine
                </h1>
                <p className="text-sm text-slate-500">{summary?.building_name || 'Financial & Maintenance Insights'}</p>
              </div>
            </div>
            <Button variant="outline" onClick={fetchAnalyticsData}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Summary Cards */}
        {summary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            {/* Cost Benchmark */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Cost vs Market</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {summary.summary?.cost_benchmark?.percentile_rank}%
                    </p>
                    <p className="text-xs text-slate-500">percentile rank</p>
                  </div>
                  <div className={`p-2 rounded-lg ${getStatusColor(summary.summary?.cost_benchmark?.status)}`}>
                    {summary.summary?.cost_benchmark?.status === 'above_avg' ? (
                      <ArrowUpRight className="w-5 h-5" />
                    ) : summary.summary?.cost_benchmark?.status === 'below_avg' ? (
                      <ArrowDownRight className="w-5 h-5" />
                    ) : (
                      <TrendingUp className="w-5 h-5" />
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Levy Forecast */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">5-Year Levy</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {formatCurrency(summary.summary?.levy_forecast?.projected_5yr || 0)}
                    </p>
                    <p className="text-xs text-slate-500">from {formatCurrency(summary.summary?.levy_forecast?.current_levy || 0)}</p>
                  </div>
                  <div className="p-2 rounded-lg bg-blue-50 text-blue-600">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Reserve Fund */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Reserve Health</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {summary.summary?.reserve_fund?.percentage}%
                    </p>
                    <p className="text-xs text-slate-500">of target</p>
                  </div>
                  <div className={`p-2 rounded-lg ${getStatusColor(summary.summary?.reserve_fund?.health)}`}>
                    <PiggyBank className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Asset Alerts */}
            <Card className="border-slate-200">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Asset Alerts</p>
                    <p className="text-2xl font-bold text-slate-900">
                      {summary.summary?.asset_alerts?.urgent_replacements || 0}
                    </p>
                    <p className="text-xs text-slate-500">need attention</p>
                  </div>
                  <div className={`p-2 rounded-lg ${getStatusColor(summary.summary?.asset_alerts?.status)}`}>
                    <Wrench className="w-5 h-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Real-Time Alerts Panel */}
        {alerts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50 mb-8" data-testid="alerts-panel">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                  <Bell className="w-5 h-5" />
                  Active Alerts ({alerts.length})
                </CardTitle>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAlertSettings(true)}
                  className="text-amber-700 hover:bg-amber-100"
                >
                  <Settings className="w-4 h-4 mr-1" />
                  Settings
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {alerts.map((alert) => (
                <div 
                  key={alert.alert_id}
                  className={`p-4 rounded-lg border flex items-start gap-3 ${
                    alert.severity === 'critical' 
                      ? 'bg-red-50 border-red-200' 
                      : 'bg-white border-amber-200'
                  }`}
                  data-testid={`alert-${alert.type}`}
                >
                  <div className={`mt-0.5 p-2 rounded-lg ${
                    alert.severity === 'critical' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'
                  }`}>
                    {getAlertTypeIcon(alert.type)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <p className={`font-semibold ${alert.severity === 'critical' ? 'text-red-800' : 'text-amber-800'}`}>
                          {alert.title}
                        </p>
                        <p className={`text-sm ${alert.severity === 'critical' ? 'text-red-600' : 'text-amber-600'}`}>
                          {alert.message}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert.severity === 'critical' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <div className="mt-2 flex items-center gap-2">
                      <p className="text-xs text-slate-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {new Date(alert.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="mt-3 p-2 bg-slate-50 rounded text-sm text-slate-600">
                      <strong>Recommendation:</strong> {alert.recommendation}
                    </div>
                    <div className="mt-3 flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => dismissAlert(alert.alert_id, 7)}
                        className="text-xs"
                      >
                        Dismiss for 7 days
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => dismissAlert(alert.alert_id, 30)}
                        className="text-xs"
                      >
                        Dismiss for 30 days
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Alert Settings Modal */}
        {showAlertSettings && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <Card className="w-full max-w-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Settings className="w-5 h-5 text-blue-600" />
                    Alert Settings
                  </CardTitle>
                  <Button variant="ghost" size="icon" onClick={() => setShowAlertSettings(false)}>
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Threshold Settings</h4>
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm">Cost Benchmark Alert (% above market avg)</Label>
                      <Input
                        type="number"
                        value={alertSettings.thresholds.cost_benchmark_threshold}
                        onChange={(e) => setAlertSettings(prev => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, cost_benchmark_threshold: parseFloat(e.target.value) || 15 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Reserve Fund Critical Level (%)</Label>
                      <Input
                        type="number"
                        value={alertSettings.thresholds.reserve_fund_critical}
                        onChange={(e) => setAlertSettings(prev => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, reserve_fund_critical: parseFloat(e.target.value) || 30 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Reserve Fund Warning Level (%)</Label>
                      <Input
                        type="number"
                        value={alertSettings.thresholds.reserve_fund_warning}
                        onChange={(e) => setAlertSettings(prev => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, reserve_fund_warning: parseFloat(e.target.value) || 50 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Levy Collection Warning (%)</Label>
                      <Input
                        type="number"
                        value={alertSettings.thresholds.levy_collection_warning}
                        onChange={(e) => setAlertSettings(prev => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, levy_collection_warning: parseFloat(e.target.value) || 85 }
                        }))}
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Urgent Asset Alert Count</Label>
                      <Input
                        type="number"
                        value={alertSettings.thresholds.asset_urgent_count}
                        onChange={(e) => setAlertSettings(prev => ({
                          ...prev,
                          thresholds: { ...prev.thresholds, asset_urgent_count: parseInt(e.target.value) || 2 }
                        }))}
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3">Notifications</h4>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Email Alerts</Label>
                      <Switch
                        checked={alertSettings.notifications.email_alerts}
                        onCheckedChange={(checked) => setAlertSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, email_alerts: checked }
                        }))}
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>Push Notifications</Label>
                      <Switch
                        checked={alertSettings.notifications.push_alerts}
                        onCheckedChange={(checked) => setAlertSettings(prev => ({
                          ...prev,
                          notifications: { ...prev.notifications, push_alerts: checked }
                        }))}
                      />
                    </div>
                  </div>
                </div>
                
                {/* Email Digest Section */}
                <div className="border-t border-slate-200 pt-4">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email Digest
                  </h4>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <Label>Enable Email Digest</Label>
                      <Switch
                        checked={digestSettings.enabled}
                        onCheckedChange={(checked) => setDigestSettings(prev => ({
                          ...prev,
                          enabled: checked
                        }))}
                      />
                    </div>
                    
                    {digestSettings.enabled && (
                      <>
                        <div>
                          <Label className="text-sm">Frequency</Label>
                          <select
                            className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
                            value={digestSettings.frequency}
                            onChange={(e) => setDigestSettings(prev => ({
                              ...prev,
                              frequency: e.target.value
                            }))}
                          >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                          </select>
                        </div>
                        
                        {digestSettings.frequency === 'weekly' && (
                          <div>
                            <Label className="text-sm">Day of Week</Label>
                            <select
                              className="w-full mt-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
                              value={digestSettings.day_of_week}
                              onChange={(e) => setDigestSettings(prev => ({
                                ...prev,
                                day_of_week: parseInt(e.target.value)
                              }))}
                            >
                              <option value={0}>Monday</option>
                              <option value={1}>Tuesday</option>
                              <option value={2}>Wednesday</option>
                              <option value={3}>Thursday</option>
                              <option value={4}>Friday</option>
                              <option value={5}>Saturday</option>
                              <option value={6}>Sunday</option>
                            </select>
                          </div>
                        )}
                        
                        {digestSettings.frequency === 'monthly' && (
                          <div>
                            <Label className="text-sm">Day of Month</Label>
                            <Input
                              type="number"
                              min="1"
                              max="28"
                              value={digestSettings.day_of_month}
                              onChange={(e) => setDigestSettings(prev => ({
                                ...prev,
                                day_of_month: parseInt(e.target.value) || 1
                              }))}
                            />
                          </div>
                        )}
                        
                        <div>
                          <Label className="text-sm">Time (Hour, 24h format)</Label>
                          <Input
                            type="number"
                            min="0"
                            max="23"
                            value={digestSettings.hour}
                            onChange={(e) => setDigestSettings(prev => ({
                              ...prev,
                              hour: parseInt(e.target.value) || 8
                            }))}
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Include Summary</Label>
                            <Switch
                              checked={digestSettings.include_summary}
                              onCheckedChange={(checked) => setDigestSettings(prev => ({
                                ...prev,
                                include_summary: checked
                              }))}
                            />
                          </div>
                          <div className="flex items-center justify-between">
                            <Label className="text-sm">Include Recommendations</Label>
                            <Switch
                              checked={digestSettings.include_recommendations}
                              onCheckedChange={(checked) => setDigestSettings(prev => ({
                                ...prev,
                                include_recommendations: checked
                              }))}
                            />
                          </div>
                        </div>
                        
                        <Button 
                          variant="outline" 
                          className="w-full" 
                          onClick={sendDigestNow}
                          disabled={sendingDigest}
                        >
                          {sendingDigest ? (
                            <>
                              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Mail className="w-4 h-4 mr-2" />
                              Send Digest Now
                            </>
                          )}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setShowAlertSettings(false)} className="flex-1">
                    Cancel
                  </Button>
                  <Button onClick={saveAlertSettings} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Save Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* Main Tabs */}
        <Tabs value={activeTab} onValueChange={(val) => {
          setActiveTab(val);
          if (val === 'trends' && !trendsData) {
            fetchTrendsData();
          }
        }} className="space-y-6">
          <TabsList className="bg-white border border-slate-200 p-1 flex-wrap">
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Overview
            </TabsTrigger>
            <TabsTrigger value="trends" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Trends
            </TabsTrigger>
            <TabsTrigger value="benchmark" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Benchmarking
            </TabsTrigger>
            <TabsTrigger value="levy" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Levy Forecast
            </TabsTrigger>
            <TabsTrigger value="reserve" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Reserve Planning
            </TabsTrigger>
            <TabsTrigger value="assets" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Asset Forecast
            </TabsTrigger>
            <TabsTrigger value="contractors" className="data-[state=active]:bg-blue-50 data-[state=active]:text-blue-700">
              Contractor Prices
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Cost Comparison Chart */}
              {benchmarkData && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Building2 className="w-5 h-5 text-blue-600" />
                      Category Cost Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={Object.entries(benchmarkData.category_benchmarks || {}).map(([key, val]) => ({
                            name: key.charAt(0).toUpperCase() + key.slice(1),
                            yours: val.your_value,
                            market: val.market_average,
                          }))}
                          layout="vertical"
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis type="number" tickFormatter={(v) => `R${v}`} />
                          <YAxis dataKey="name" type="category" width={80} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Legend />
                          <Bar dataKey="yours" name="Your Complex" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                          <Bar dataKey="market" name="Market Avg" fill="#94a3b8" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Levy Projection Chart */}
              {levyForecast?.forecast?.forecasts && (
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-emerald-600" />
                      Levy Projection
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                          data={[
                            { year: 'Current', levy: levyForecast.current_levy || levyForecast.forecast?.starting_levy },
                            ...levyForecast.forecast.forecasts.map(f => ({
                              year: f.current_year,
                              levy: f.projected_levy,
                            }))
                          ]}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                          <XAxis dataKey="year" />
                          <YAxis tickFormatter={(v) => `R${v}`} />
                          <Tooltip formatter={(v) => formatCurrency(v)} />
                          <Line 
                            type="monotone" 
                            dataKey="levy" 
                            stroke="#10b981" 
                            strokeWidth={3}
                            dot={{ fill: '#10b981', r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            
            {/* Insights */}
            {benchmarkData?.insights && (
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Key Insights</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {benchmarkData.insights.map((insight, idx) => (
                      <div 
                        key={idx}
                        className={`p-4 rounded-lg flex items-start gap-3 ${
                          insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                          insight.type === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                          'bg-blue-50 border border-blue-200'
                        }`}
                      >
                        <div className={`mt-0.5 ${
                          insight.type === 'warning' ? 'text-amber-600' :
                          insight.type === 'positive' ? 'text-emerald-600' :
                          'text-blue-600'
                        }`}>
                          {insight.type === 'warning' ? <AlertTriangle className="w-5 h-5" /> :
                           insight.type === 'positive' ? <CheckCircle2 className="w-5 h-5" /> :
                           <Info className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{insight.title}</p>
                          <p className="text-sm text-slate-600">{insight.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Trends Tab */}
          <TabsContent value="trends" className="space-y-6">
            {loadingTrends ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              </div>
            ) : trendsData ? (
              <>
                {/* Comparison Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  {trendsData.comparisons?.month_over_month && (
                    <>
                      <Card className="border-slate-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-slate-500 mb-2">Costs vs Last Month</p>
                          <div className="flex items-center gap-2">
                            <span className={`p-1.5 rounded ${getTrendColor(trendsData.comparisons.month_over_month.total_costs.direction)}`}>
                              {getTrendIcon(trendsData.comparisons.month_over_month.total_costs.direction)}
                            </span>
                            <span className="text-2xl font-bold">
                              {trendsData.comparisons.month_over_month.total_costs.percentage > 0 ? '+' : ''}
                              {trendsData.comparisons.month_over_month.total_costs.percentage}%
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-slate-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-slate-500 mb-2">Levy vs Last Month</p>
                          <div className="flex items-center gap-2">
                            <span className={`p-1.5 rounded ${getTrendColor(trendsData.comparisons.month_over_month.levy.direction)}`}>
                              {getTrendIcon(trendsData.comparisons.month_over_month.levy.direction)}
                            </span>
                            <span className="text-2xl font-bold">
                              {trendsData.comparisons.month_over_month.levy.percentage > 0 ? '+' : ''}
                              {trendsData.comparisons.month_over_month.levy.percentage}%
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-slate-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-slate-500 mb-2">Collection Rate</p>
                          <div className="flex items-center gap-2">
                            <span className={`p-1.5 rounded ${getTrendColor(trendsData.comparisons.month_over_month.collection_rate.direction, true)}`}>
                              {getTrendIcon(trendsData.comparisons.month_over_month.collection_rate.direction)}
                            </span>
                            <span className="text-2xl font-bold">
                              {trendsData.comparisons.month_over_month.collection_rate.percentage > 0 ? '+' : ''}
                              {trendsData.comparisons.month_over_month.collection_rate.percentage}%
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                      <Card className="border-slate-200">
                        <CardContent className="p-4">
                          <p className="text-sm text-slate-500 mb-2">Reserve Fund</p>
                          <div className="flex items-center gap-2">
                            <span className={`p-1.5 rounded ${getTrendColor(trendsData.comparisons.month_over_month.reserve_fund.direction, true)}`}>
                              {getTrendIcon(trendsData.comparisons.month_over_month.reserve_fund.direction)}
                            </span>
                            <span className="text-2xl font-bold">
                              {trendsData.comparisons.month_over_month.reserve_fund.percentage > 0 ? '+' : ''}
                              {trendsData.comparisons.month_over_month.reserve_fund.percentage}%
                            </span>
                          </div>
                        </CardContent>
                      </Card>
                    </>
                  )}
                </div>
                
                {/* Trend Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Costs & Levy Trend */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        Cost & Levy Trends
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendsData.chart_data?.labels?.map((label, idx) => ({
                            period: label,
                            costs: trendsData.chart_data.costs[idx],
                            levy: trendsData.chart_data.levy[idx],
                          })) || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={(v) => `R${v}`} />
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Legend />
                            <Line type="monotone" dataKey="costs" name="Costs/Unit" stroke="#3b82f6" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="levy" name="Levy/Unit" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                  
                  {/* Collection & Reserve Trend */}
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <PiggyBank className="w-5 h-5 text-emerald-600" />
                        Collection & Reserve Trends
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={trendsData.chart_data?.labels?.map((label, idx) => ({
                            period: label,
                            collection: trendsData.chart_data.collection_rate[idx],
                            reserve: trendsData.chart_data.reserve_percentage[idx],
                          })) || []}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                            <YAxis tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                            <Tooltip formatter={(v) => `${v}%`} />
                            <Legend />
                            <Line type="monotone" dataKey="collection" name="Collection Rate" stroke="#f59e0b" strokeWidth={2} dot={{ r: 4 }} />
                            <Line type="monotone" dataKey="reserve" name="Reserve %" stroke="#8b5cf6" strokeWidth={2} dot={{ r: 4 }} />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Insights */}
                {trendsData.insights && trendsData.insights.length > 0 && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Trend Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {trendsData.insights.map((insight, idx) => (
                          <div 
                            key={idx}
                            className={`p-4 rounded-lg flex items-start gap-3 ${
                              insight.type === 'warning' ? 'bg-amber-50 border border-amber-200' :
                              insight.type === 'positive' ? 'bg-emerald-50 border border-emerald-200' :
                              'bg-blue-50 border border-blue-200'
                            }`}
                          >
                            <div className={`mt-0.5 ${
                              insight.type === 'warning' ? 'text-amber-600' :
                              insight.type === 'positive' ? 'text-emerald-600' :
                              'text-blue-600'
                            }`}>
                              {insight.type === 'warning' ? <AlertTriangle className="w-5 h-5" /> :
                               insight.type === 'positive' ? <CheckCircle2 className="w-5 h-5" /> :
                               <Info className="w-5 h-5" />}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-semibold text-slate-900">{insight.title}</p>
                                <span className={`text-xs px-2 py-0.5 rounded ${
                                  insight.impact === 'high' ? 'bg-red-100 text-red-700' :
                                  insight.impact === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {insight.impact} impact
                                </span>
                              </div>
                              <p className="text-sm text-slate-600">{insight.message}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Period Comparison */}
                {trendsData.comparisons?.period_comparison && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        {trendsData.analysis_period} Summary
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-3 px-4 font-semibold">Metric</th>
                              <th className="text-right py-3 px-4 font-semibold">Start of Period</th>
                              <th className="text-right py-3 px-4 font-semibold">Current</th>
                              <th className="text-right py-3 px-4 font-semibold">Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">Total Costs/Unit</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(trendsData.snapshots?.[trendsData.snapshots.length-1]?.metrics?.total_cost_per_unit || 0)}</td>
                              <td className="py-3 px-4 text-right font-semibold">{formatCurrency(trendsData.current_metrics?.total_cost_per_unit || 0)}</td>
                              <td className={`py-3 px-4 text-right font-semibold ${
                                trendsData.comparisons.period_comparison.total_costs.direction === 'down' ? 'text-emerald-600' : 
                                trendsData.comparisons.period_comparison.total_costs.direction === 'up' ? 'text-red-600' : ''
                              }`}>
                                {trendsData.comparisons.period_comparison.total_costs.percentage > 0 ? '+' : ''}
                                {trendsData.comparisons.period_comparison.total_costs.percentage}%
                              </td>
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">Levy/Unit</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(trendsData.snapshots?.[trendsData.snapshots.length-1]?.metrics?.levy_per_unit || 0)}</td>
                              <td className="py-3 px-4 text-right font-semibold">{formatCurrency(trendsData.current_metrics?.levy_per_unit || 0)}</td>
                              <td className={`py-3 px-4 text-right font-semibold ${
                                trendsData.comparisons.period_comparison.levy.direction === 'down' ? 'text-emerald-600' : 
                                trendsData.comparisons.period_comparison.levy.direction === 'up' ? 'text-red-600' : ''
                              }`}>
                                {trendsData.comparisons.period_comparison.levy.percentage > 0 ? '+' : ''}
                                {trendsData.comparisons.period_comparison.levy.percentage}%
                              </td>
                            </tr>
                            <tr className="border-b border-slate-100">
                              <td className="py-3 px-4">Collection Rate</td>
                              <td className="py-3 px-4 text-right">{trendsData.snapshots?.[trendsData.snapshots.length-1]?.metrics?.collection_rate || 0}%</td>
                              <td className="py-3 px-4 text-right font-semibold">{trendsData.current_metrics?.collection_rate || 0}%</td>
                              <td className={`py-3 px-4 text-right font-semibold ${
                                trendsData.comparisons.period_comparison.collection_rate.direction === 'up' ? 'text-emerald-600' : 
                                trendsData.comparisons.period_comparison.collection_rate.direction === 'down' ? 'text-red-600' : ''
                              }`}>
                                {trendsData.comparisons.period_comparison.collection_rate.percentage > 0 ? '+' : ''}
                                {trendsData.comparisons.period_comparison.collection_rate.percentage}%
                              </td>
                            </tr>
                            <tr>
                              <td className="py-3 px-4">Reserve Fund %</td>
                              <td className="py-3 px-4 text-right">{trendsData.snapshots?.[trendsData.snapshots.length-1]?.metrics?.reserve_percentage || 0}%</td>
                              <td className="py-3 px-4 text-right font-semibold">{trendsData.current_metrics?.reserve_percentage || 0}%</td>
                              <td className={`py-3 px-4 text-right font-semibold ${
                                trendsData.comparisons.period_comparison.reserve_fund.direction === 'up' ? 'text-emerald-600' : 
                                trendsData.comparisons.period_comparison.reserve_fund.direction === 'down' ? 'text-red-600' : ''
                              }`}>
                                {trendsData.comparisons.period_comparison.reserve_fund.percentage > 0 ? '+' : ''}
                                {trendsData.comparisons.period_comparison.reserve_fund.percentage}%
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            ) : (
              <Card className="border-slate-200">
                <CardContent className="p-12 text-center">
                  <TrendingUp className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">Click the tab to load trend data</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          {/* Benchmarking Tab */}
          <TabsContent value="benchmark" className="space-y-6">
            {benchmarkData && (
              <>
                {/* Your Position */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Your Complex vs Market</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="text-center p-6 bg-slate-50 rounded-xl">
                        <p className="text-sm text-slate-500 mb-2">Your Cost/Unit</p>
                        <p className="text-3xl font-bold text-slate-900">
                          {formatCurrency(benchmarkData.your_complex?.cost_per_unit || 0)}
                        </p>
                      </div>
                      <div className="text-center p-6 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-600 mb-2">Market Average</p>
                        <p className="text-3xl font-bold text-blue-700">
                          {formatCurrency(benchmarkData.benchmark_summary?.average_cost_per_unit || 0)}
                        </p>
                      </div>
                      <div className="text-center p-6 bg-emerald-50 rounded-xl">
                        <p className="text-sm text-emerald-600 mb-2">Your Percentile</p>
                        <p className="text-3xl font-bold text-emerald-700">
                          {benchmarkData.your_complex?.percentile}%
                        </p>
                        <p className="text-xs text-emerald-600 mt-1">
                          Lower is better
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Category Breakdown */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Category Analysis</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(benchmarkData.category_benchmarks || {}).map(([category, data]) => (
                        <div key={category} className="p-4 bg-slate-50 rounded-lg">
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="font-semibold text-slate-900 capitalize">{category}</h4>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(data.rating)}`}>
                              {data.variance_from_avg > 0 ? '+' : ''}{data.variance_from_avg}% vs avg
                            </span>
                          </div>
                          <div className="grid grid-cols-4 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Your Cost</p>
                              <p className="font-semibold">{formatCurrency(data.your_value)}/unit</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Market Avg</p>
                              <p className="font-semibold">{formatCurrency(data.market_average)}/unit</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Market Min</p>
                              <p className="font-semibold text-emerald-600">{formatCurrency(data.market_min)}/unit</p>
                            </div>
                            <div>
                              <p className="text-slate-500">Market Max</p>
                              <p className="font-semibold text-red-600">{formatCurrency(data.market_max)}/unit</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Peer Comparison */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Users className="w-5 h-5 text-slate-600" />
                      Anonymized Peer Comparison
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold text-slate-600">Complex</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-600">Units</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-600">Cost/Unit</th>
                            <th className="text-right py-3 px-4 font-semibold text-slate-600">Levy/Unit</th>
                            <th className="text-left py-3 px-4 font-semibold text-slate-600">Region</th>
                          </tr>
                        </thead>
                        <tbody>
                          {benchmarkData.peer_complexes?.map((peer, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4 font-medium">{peer.name}</td>
                              <td className="py-3 px-4 text-right">{peer.unit_count}</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(peer.cost_per_unit)}</td>
                              <td className="py-3 px-4 text-right">{formatCurrency(peer.levy_per_unit)}</td>
                              <td className="py-3 px-4">{peer.region}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-slate-500 mt-4 italic">
                      {benchmarkData.data_disclaimer}
                    </p>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
          
          {/* Levy Forecast Tab */}
          <TabsContent value="levy" className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Forecast Controls */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg">Forecast Parameters</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label>Current Monthly Levy (R)</Label>
                    <Input
                      type="number"
                      value={forecastInputs.currentLevy}
                      onChange={(e) => setForecastInputs(prev => ({ ...prev, currentLevy: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div>
                    <Label>Inflation Rate (%)</Label>
                    <Input
                      type="number"
                      step="0.5"
                      value={forecastInputs.inflationRate}
                      onChange={(e) => setForecastInputs(prev => ({ ...prev, inflationRate: parseFloat(e.target.value) || 6 }))}
                    />
                  </div>
                  <div>
                    <Label>Years Ahead</Label>
                    <Input
                      type="number"
                      min="1"
                      max="10"
                      value={forecastInputs.yearsAhead}
                      onChange={(e) => setForecastInputs(prev => ({ ...prev, yearsAhead: parseInt(e.target.value) || 5 }))}
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Include Reserve Buildup</Label>
                    <Switch
                      checked={forecastInputs.includeReserve}
                      onCheckedChange={(checked) => setForecastInputs(prev => ({ ...prev, includeReserve: checked }))}
                    />
                  </div>
                  <Button onClick={runCustomForecast} className="w-full">
                    <TrendingUp className="w-4 h-4 mr-2" />
                    Run Forecast
                  </Button>
                </CardContent>
              </Card>
              
              {/* Forecast Results */}
              <Card className="border-slate-200 lg:col-span-2">
                <CardHeader>
                  <CardTitle className="text-lg">Levy Projection</CardTitle>
                </CardHeader>
                <CardContent>
                  {levyForecast?.forecast?.forecasts && (
                    <>
                      <div className="h-64 mb-6">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={[
                              { year: 'Now', levy: levyForecast.current_levy || levyForecast.forecast?.starting_levy, increase: 0 },
                              ...levyForecast.forecast.forecasts.map(f => ({
                                year: f.current_year,
                                levy: f.projected_levy,
                                increase: f.cumulative_increase,
                              }))
                            ]}
                          >
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="year" />
                            <YAxis yAxisId="left" tickFormatter={(v) => `R${v}`} />
                            <YAxis yAxisId="right" orientation="right" tickFormatter={(v) => `${v}%`} />
                            <Tooltip />
                            <Legend />
                            <Line 
                              yAxisId="left"
                              type="monotone" 
                              dataKey="levy" 
                              name="Monthly Levy"
                              stroke="#3b82f6" 
                              strokeWidth={3}
                              dot={{ fill: '#3b82f6', r: 5 }}
                            />
                            <Line 
                              yAxisId="right"
                              type="monotone" 
                              dataKey="increase" 
                              name="Cumulative %"
                              stroke="#10b981" 
                              strokeWidth={2}
                              strokeDasharray="5 5"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="text-left py-2 px-3 font-semibold">Year</th>
                              <th className="text-right py-2 px-3 font-semibold">Projected Levy</th>
                              <th className="text-right py-2 px-3 font-semibold">Annual Increase</th>
                              <th className="text-right py-2 px-3 font-semibold">Cumulative</th>
                            </tr>
                          </thead>
                          <tbody>
                            {levyForecast.forecast.forecasts.map((f, idx) => (
                              <tr key={idx} className="border-b border-slate-100">
                                <td className="py-2 px-3">{f.current_year}</td>
                                <td className="py-2 px-3 text-right font-semibold">{formatCurrency(f.projected_levy)}</td>
                                <td className="py-2 px-3 text-right text-amber-600">+{f.increase_percentage}%</td>
                                <td className="py-2 px-3 text-right text-blue-600">+{f.cumulative_increase}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Reserve Planning Tab */}
          <TabsContent value="reserve" className="space-y-6">
            {reservePlan && (
              <>
                {/* Current Status */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-6 text-center">
                      <PiggyBank className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 mb-1">Current Balance</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(reservePlan.current_status?.balance || 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-6 text-center">
                      <Target className="w-8 h-8 text-emerald-600 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 mb-1">Target Amount</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {formatCurrency(reservePlan.current_status?.target || 0)}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-slate-200">
                    <CardContent className="p-6 text-center">
                      <Calendar className="w-8 h-8 text-amber-600 mx-auto mb-3" />
                      <p className="text-sm text-slate-500 mb-1">Years to Target</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {reservePlan.projection?.years_to_target || 'N/A'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Scenarios */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Contribution Scenarios</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {Object.entries(reservePlan.scenarios || {}).map(([key, scenario]) => (
                        <div 
                          key={key}
                          className={`p-4 rounded-xl border-2 ${
                            key === 'current' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-slate-50'
                          }`}
                        >
                          <h4 className="font-semibold text-slate-900 capitalize mb-3">{key}</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-slate-500">Monthly</span>
                              <span className="font-semibold">{formatCurrency(scenario.contribution)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-slate-500">Years to Target</span>
                              <span className="font-semibold">{scenario.years_to_target || 'N/A'}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
                
                {/* Projections Chart */}
                {reservePlan.projection?.yearly_projections && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg">10-Year Projection</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={reservePlan.projection.yearly_projections}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis dataKey="year" tickFormatter={(v) => `Year ${v}`} />
                            <YAxis tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                            <Tooltip formatter={(v) => formatCurrency(v)} />
                            <Line 
                              type="monotone" 
                              dataKey="balance" 
                              stroke="#3b82f6" 
                              strokeWidth={3}
                              dot={{ fill: '#3b82f6', r: 5 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}
                
                {/* Recommendations */}
                {reservePlan.projection?.recommendations && (
                  <Card className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg">Recommendations</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {reservePlan.projection.recommendations.map((rec, idx) => (
                          <div 
                            key={idx}
                            className={`p-4 rounded-lg border ${
                              rec.priority === 'high' ? 'bg-red-50 border-red-200' :
                              rec.priority === 'medium' ? 'bg-amber-50 border-amber-200' :
                              rec.priority === 'low' ? 'bg-blue-50 border-blue-200' :
                              'bg-emerald-50 border-emerald-200'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              {getStatusIcon(rec.priority === 'high' ? 'critical' : rec.priority === 'medium' ? 'attention' : 'ok')}
                              <div>
                                <p className="font-semibold text-slate-900">{rec.title}</p>
                                <p className="text-sm text-slate-600 mb-2">{rec.description}</p>
                                <p className="text-sm font-medium text-slate-700">
                                  <ChevronRight className="w-4 h-4 inline" /> {rec.action}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
          
          {/* Asset Forecast Tab */}
          <TabsContent value="assets" className="space-y-6">
            {assetForecast && (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="border-slate-200">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-slate-500 mb-1">Total Assets</p>
                      <p className="text-2xl font-bold text-slate-900">
                        {assetForecast.forecast?.assets?.length || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-red-200 bg-red-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-red-600 mb-1">Immediate</p>
                      <p className="text-2xl font-bold text-red-700">
                        {assetForecast.forecast?.immediate_attention_count || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-amber-200 bg-amber-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-amber-600 mb-1">Urgent (2 yrs)</p>
                      <p className="text-2xl font-bold text-amber-700">
                        {assetForecast.forecast?.urgent_count || 0}
                      </p>
                    </CardContent>
                  </Card>
                  <Card className="border-blue-200 bg-blue-50">
                    <CardContent className="p-4 text-center">
                      <p className="text-sm text-blue-600 mb-1">10-Year Cost</p>
                      <p className="text-xl font-bold text-blue-700">
                        {formatCurrency(assetForecast.forecast?.total_10_year_replacement_cost || 0)}
                      </p>
                    </CardContent>
                  </Card>
                </div>
                
                {/* Budgeting Recommendation */}
                <Card className="border-emerald-200 bg-emerald-50">
                  <CardContent className="p-6">
                    <h3 className="font-semibold text-emerald-800 mb-3 flex items-center gap-2">
                      <DollarSign className="w-5 h-5" />
                      Budgeting Recommendation
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <p className="text-sm text-emerald-700">Annual Reserve Needed</p>
                        <p className="text-2xl font-bold text-emerald-900">
                          {formatCurrency(assetForecast.budgeting_recommendation?.annual_reserve_needed || 0)}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-emerald-700">Monthly Contribution</p>
                        <p className="text-2xl font-bold text-emerald-900">
                          {formatCurrency(assetForecast.budgeting_recommendation?.monthly_contribution || 0)}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Asset List */}
                <Card className="border-slate-200">
                  <CardHeader>
                    <CardTitle className="text-lg">Asset Replacement Schedule</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-slate-200">
                            <th className="text-left py-3 px-4 font-semibold">Asset</th>
                            <th className="text-left py-3 px-4 font-semibold">Category</th>
                            <th className="text-center py-3 px-4 font-semibold">Condition</th>
                            <th className="text-right py-3 px-4 font-semibold">Replace In</th>
                            <th className="text-right py-3 px-4 font-semibold">Est. Cost</th>
                            <th className="text-center py-3 px-4 font-semibold">Urgency</th>
                          </tr>
                        </thead>
                        <tbody>
                          {assetForecast.forecast?.assets?.map((asset, idx) => (
                            <tr key={idx} className="border-b border-slate-100 hover:bg-slate-50">
                              <td className="py-3 px-4 font-medium">{asset.name}</td>
                              <td className="py-3 px-4 text-slate-600">{asset.category}</td>
                              <td className="py-3 px-4 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  asset.current_condition === 'good' ? 'bg-emerald-100 text-emerald-700' :
                                  asset.current_condition === 'fair' ? 'bg-amber-100 text-amber-700' :
                                  'bg-red-100 text-red-700'
                                }`}>
                                  {asset.current_condition}
                                </span>
                              </td>
                              <td className="py-3 px-4 text-right">
                                {asset.years_until_replacement === 0 ? 'Now' : `${asset.years_until_replacement} yrs`}
                              </td>
                              <td className="py-3 px-4 text-right font-semibold">
                                {formatCurrency(asset.estimated_replacement_cost)}
                              </td>
                              <td className="py-3 px-4 text-center">
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  asset.urgency === 'immediate' ? 'bg-red-100 text-red-700' :
                                  asset.urgency === 'urgent' ? 'bg-amber-100 text-amber-700' :
                                  asset.urgency === 'planned' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {asset.urgency}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>
          
          {/* Contractor Prices Tab */}
          <TabsContent value="contractors" className="space-y-6">
            {contractorPrices && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {Object.entries(contractorPrices.categories || {}).map(([category, data]) => (
                  <Card key={category} className="border-slate-200">
                    <CardHeader>
                      <CardTitle className="text-lg capitalize flex items-center gap-2">
                        <Shield className="w-5 h-5 text-blue-600" />
                        {category}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {data.service_call && data.service_call[0] > 0 && (
                        <div className="mb-4 p-3 bg-slate-50 rounded-lg">
                          <div className="flex justify-between text-sm">
                            <span className="text-slate-500">Service Call</span>
                            <span className="font-semibold">{formatCurrency(data.service_call[0])} - {formatCurrency(data.service_call[1])}</span>
                          </div>
                          {data.hourly_rate && data.hourly_rate[0] > 0 && (
                            <div className="flex justify-between text-sm mt-2">
                              <span className="text-slate-500">Hourly Rate</span>
                              <span className="font-semibold">{formatCurrency(data.hourly_rate[0])} - {formatCurrency(data.hourly_rate[1])}</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-slate-700">Common Jobs</p>
                        {data.common_jobs?.map((job, idx) => (
                          <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                            <span className="text-sm text-slate-600">{job.name}</span>
                            <span className="text-sm font-semibold text-slate-900">
                              {formatCurrency(job.range[0])} - {formatCurrency(job.range[1])}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {contractorPrices && (
              <p className="text-xs text-slate-500 italic text-center">
                {contractorPrices.disclaimer}
              </p>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
